import React from 'react';
import { Button, Space, Modal, Form, Input, Select, Card, Tag } from 'antd';
import {
  PlusOutlined,
  DatabaseOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Service } from '@/apis';
import type { LogDetailResponse, LogSaveDto, LogPutDto } from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { useFormModal } from '@/hooks/useFormModal';
import { SearchBar } from '@/components/SearchBar';
import { createTimeColumn, createLongTextColumn } from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

// 使用API返回的数据类型
type DataItem = LogDetailResponse;

const LogDataManagement: React.FC = () => {
  // 使用带搜索功能的表格管理Hook
  const {
    data: dataList,
    loading,
    pagination,
    fetchData: fetchDataList,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<DataItem>({
    fetchData: (pageNum, pageSize, custom) =>
      Service.logControllerGet(pageNum, pageSize, custom),
    deleteData: async (id: number) => {
      await Service.logControllerDeleteLog(id);
    },
    errorMessage: '获取日志数据列表失败',
    searchOptions: {
      fields: ['uid', 'cuid', 'name', 'channel', 'ip'],
    },
  });

  // 使用Modal管理Hook
  const {
    visible,
    editingItem: editingData,
    form,
    handleAdd,
    handleEdit,
    handleOk,
    handleCancel,
  } = useFormModal<DataItem>({
    createData: async (data: LogSaveDto) => {
      await Service.logControllerSave(data);
    },
    updateData: async (data: LogPutDto) => {
      await Service.logControllerPut(data);
    },
  });

  // 处理Modal确认后的数据刷新
  const handleModalOkWithRefresh = async () => {
    const success = await handleOk();
    if (success) {
      fetchDataList();
    }
  };

  const columns: ColumnsType<DataItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    createLongTextColumn('点位', 'name', 10),
    {
      title: '事件类型',
      dataIndex: 't',
      key: 't',
      width: 100,
      render: (t: string) => {
        const typeMap: { [key: string]: { text: string; color: string } } = {
          '1': { text: '展现', color: 'blue' },
          '2': { text: '手动', color: 'green' },
          '3': { text: '停留', color: 'orange' },
          '4': { text: '离开', color: 'red' },
        };
        const typeInfo = typeMap[t] || { text: '未知', color: 'default' };
        return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
      },
    },
    createLongTextColumn('用户ID', 'uid', 12),
    createLongTextColumn('设备ID', 'cuid', 12),
    createLongTextColumn('页面地址', 'puid', 15),
    createLongTextColumn('手机型号', 'mc', 10),
    createLongTextColumn('手机品牌', 'br', 10),
    createLongTextColumn('设备类型', 'pl', 10),
    createLongTextColumn('渠道', 'channel', 10),
    createLongTextColumn('IP地址', 'ip', 12),
    createLongTextColumn('来源', 'referer', 15),
    createTimeColumn('full'),
  ];

  // 统计卡片数据
  const stats: DataStat[] = [
    {
      title: '总日志数据量',
      value: pagination.total,
      prefix: <DatabaseOutlined />,
      valueStyle: { color: '#3f8600' },
    },
    {
      title: '当前页日志数据',
      value: dataList.length,
      valueStyle: { color: '#1890ff' },
    },
    {
      title: '展现事件',
      value: dataList.filter(item => item.t === '1').length,
      valueStyle: { color: '#1890ff' },
    },
    {
      title: '手动事件',
      value: dataList.filter(item => item.t === '2').length,
      valueStyle: { color: '#52c41a' },
    },
    {
      title: '停留事件',
      value: dataList.filter(item => item.t === '3').length,
      valueStyle: { color: '#fa8c16' },
    },
    {
      title: '离开事件',
      value: dataList.filter(item => item.t === '4').length,
      valueStyle: { color: '#f5222d' },
    },
  ];

  // 操作按钮配置
  const actionButtons: ActionButton<DataItem>[] = [
    {
      key: 'view',
      label: '查看',
      icon: <EyeOutlined />,
      onClick: record => {
        Modal.info({
          title: '日志详情',
          width: 800,
          content: (
            <div>
              <p>
                <strong>ID:</strong> {record.id}
              </p>
              <p>
                <strong>点位:</strong> {record.name || '-'}
              </p>
              <p>
                <strong>事件类型:</strong>{' '}
                {record.t === '1'
                  ? '展现'
                  : record.t === '2'
                    ? '手动'
                    : record.t === '3'
                      ? '停留'
                      : record.t === '4'
                        ? '离开'
                        : '未知'}
              </p>
              <p>
                <strong>用户ID:</strong> {record.uid || '-'}
              </p>
              <p>
                <strong>设备ID:</strong> {record.cuid || '-'}
              </p>
              <p>
                <strong>页面地址:</strong> {record.puid || '-'}
              </p>
              <p>
                <strong>手机型号:</strong> {record.mc || '-'}
              </p>
              <p>
                <strong>手机品牌:</strong> {record.br || '-'}
              </p>
              <p>
                <strong>设备像素比:</strong> {record.dpi || '-'}
              </p>
              <p>
                <strong>设备类型:</strong> {record.pl || '-'}
              </p>
              <p>
                <strong>扩展信息:</strong> {record.kv || '-'}
              </p>
              <p>
                <strong>渠道:</strong> {record.channel || '-'}
              </p>
              <p>
                <strong>联网类型:</strong> {record.inty || '-'}
              </p>
              <p>
                <strong>网络运营商:</strong> {record.opty || '-'}
              </p>
              <p>
                <strong>IP地址:</strong> {record.ip || '-'}
              </p>
              <p>
                <strong>来源:</strong> {record.referer || '-'}
              </p>
              <p>
                <strong>创建时间:</strong>{' '}
                {new Date(record.createTime).toLocaleString()}
              </p>
            </div>
          ),
        });
      },
    },
    {
      key: 'edit',
      label: '编辑',
      icon: <EditOutlined />,
      onClick: handleEdit,
    },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: record => handleDelete(record.id),
      confirm: {
        title: '确定要删除这条数据吗？',
        okText: '确定',
        cancelText: '取消',
      },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        {/* 统计卡片 */}
        <DataStats stats={stats} className={styles.statsRow} />

        {/* 操作栏 */}
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
              新增日志数据
            </Button>

            {/* 搜索区域 */}
            <SearchBar
              search={search}
              placeholder='搜索用户ID、设备ID、点位、渠道或IP地址'
              width={300}
              loading={loading}
            />
          </Space>
        </div>

        {/* 日志数据表格 */}
        <DataTable
          columns={columns}
          dataSource={dataList}
          loading={loading}
          rowKey='id'
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>

      {/* 新增/编辑日志数据弹窗 */}
      <Modal
        title={editingData ? '编辑日志数据' : '新增日志数据'}
        open={visible}
        onOk={handleModalOkWithRefresh}
        onCancel={handleCancel}
        width={600}
      >
        <Form form={form} layout='vertical'>
          <Form.Item name='name' label='点位'>
            <Input placeholder='请输入点位' />
          </Form.Item>
          <Form.Item
            name='t'
            label='事件类型'
            rules={[{ required: true, message: '请选择事件类型' }]}
          >
            <Select placeholder='请选择事件类型'>
              <Select.Option value='1'>展现</Select.Option>
              <Select.Option value='2'>手动</Select.Option>
              <Select.Option value='3'>停留</Select.Option>
              <Select.Option value='4'>离开</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name='uid'
            label='用户ID'
            rules={[{ required: true, message: '请输入用户ID' }]}
          >
            <Input placeholder='请输入用户ID' />
          </Form.Item>
          <Form.Item
            name='cuid'
            label='设备ID'
            rules={[{ required: true, message: '请输入设备ID' }]}
          >
            <Input placeholder='请输入设备ID' />
          </Form.Item>
          <Form.Item name='puid' label='页面地址'>
            <Input placeholder='请输入页面地址' />
          </Form.Item>
          <Form.Item name='mc' label='手机型号'>
            <Input placeholder='请输入手机型号' />
          </Form.Item>
          <Form.Item name='br' label='手机品牌'>
            <Input placeholder='请输入手机品牌' />
          </Form.Item>
          <Form.Item name='dpi' label='设备像素比'>
            <Input placeholder='请输入设备像素比' />
          </Form.Item>
          <Form.Item name='pl' label='设备类型'>
            <Input placeholder='请输入设备类型' />
          </Form.Item>
          <Form.Item name='kv' label='扩展信息'>
            <Input.TextArea placeholder='请输入扩展信息' rows={3} />
          </Form.Item>
          <Form.Item name='channel' label='渠道'>
            <Input placeholder='请输入渠道' />
          </Form.Item>
          <Form.Item name='inty' label='联网类型'>
            <Input placeholder='请输入联网类型' />
          </Form.Item>
          <Form.Item name='opty' label='网络运营商编号'>
            <Input placeholder='请输入网络运营商编号' />
          </Form.Item>
          <Form.Item
            name='ip'
            label='IP地址'
            rules={[{ required: true, message: '请输入IP地址' }]}
          >
            <Input placeholder='请输入IP地址' />
          </Form.Item>
          <Form.Item name='referer' label='来源'>
            <Input placeholder='请输入来源信息' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LogDataManagement;
